import GameError, { GameErrorType } from "@/libs/GameError";
import type { TileCoordinates, TileLabelSkin } from "@/types/tile";
import { type Reward, type TileIndex } from "@/types/tile";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { computed, type ComputedRef } from "vue";

export enum SiegeSystemResult {
	VICTORY = "victory",
	DEFEAT = "defeat",
}

type UnitData = {
	hp: number;
};

export type SiegeUnitLabelData = {
	coordinates: TileCoordinates;
	skin:
		| TileLabelSkin.FIGHT_SYSTEM_DEFEATED_ENEMY
		| TileLabelSkin.FIGHT_SYSTEM_DEFEATED_FRIENDLY;
};

export type SiegeSystemStoreState = {
	tutorialSeen: boolean;
	stats: {
		startedAt: number | null;
		endedAt: number | null;
		damageDealt: number;
		damageTaken: number;
		enemiesDefeated: number;
		enemiesTotal: number;
	};
	result: SiegeSystemResult | null;
	siegeTargetTileIndexes: Array<TileIndex>;
	mainEnemyTileIndex: TileIndex | null;
	canEnemyAttackGuard: boolean;
	units: Record<`${TileIndex}`, UnitData>;
	rewards: Reward | null;
	attackClicks: number;
	movingUnitsIndex: TileIndex | null;
	defeatedUnits: Array<SiegeUnitLabelData>;
	currentBattlefield: {
		tileIndex?: TileIndex;
		range?: number;
	};
	// offlineBoosters: number;
};

type SiegeSystemStorePayload = {
	state: SiegeSystemStoreState;
};

type SiegeSystemStore = {
	$getPayload: () => SiegeSystemStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<SiegeSystemStoreState>) => void;
	reset: () => void;
	tutorialSeen: ComputedRef<boolean>;
	setTutorialSeen: (value: boolean) => void;
	stats: ComputedRef<{
		startedAt: number | null;
		endedAt: number | null;
		damageDealt: number;
		damageTaken: number;
		enemiesDefeated: number;
		enemiesTotal: number;
	}>;
	setStartedAt: (value: number | null) => void;
	setEndedAt: (value: number | null) => void;
	increaseDamageDealt: (value: number) => void;
	increaseDamageTaken: (value: number) => void;
	increaseEnemiesDefeated: (value?: number) => void;
	setEnemiesTotal: (value: number) => void;
	result: ComputedRef<SiegeSystemResult | null>;
	setResult: (result: SiegeSystemResult | null) => void;
	siegeTargetTileIndexes: ComputedRef<Array<TileIndex>>;
	setSiegeTargetTileIndexes: (tileIndexes: Array<TileIndex>) => void;
	addSiegeTargetTileIndex: (tileIndex: TileIndex) => void;
	removeSiegeTargetTileIndex: (tileIndex: TileIndex) => void;
	mainEnemyTileIndex: ComputedRef<TileIndex | null>;
	setMainEnemyTileIndex: (tileIndex: TileIndex | null) => void;
	currentBattlefield: ComputedRef<{
		tileIndex?: TileIndex;
		range?: number;
	}>;
	setCurrentBattlefield: (value: {
		tileIndex?: TileIndex;
		range?: number;
	}) => void;
	canEnemyAttackGuard: ComputedRef<boolean>;
	setCanEnemyAttackGuard: (value: boolean) => void;
	units: ComputedRef<SiegeSystemStoreState["units"]>;
	setUnits: (units: SiegeSystemStoreState["units"] | null) => void;
	setUnit: (tileIndex: TileIndex, props: { hp: number }) => void;
	removeUnit: (tileIndex: TileIndex) => void;
	isUnitInStore: (tileIndex: TileIndex) => boolean;
	rewards: ComputedRef<Reward | null>;
	setRewards: (rewards: Reward | null) => void;
	attackClicks: ComputedRef<number>;
	setAttackClicks: (attackClicks: number) => void;
	movingUnitsIndex: ComputedRef<TileIndex | null>;
	setMovingUnitsIndex: (movingUnitsIndex: TileIndex | null) => void;
	defeatedUnits: ComputedRef<Array<SiegeUnitLabelData>>;
	setDefeatedUnits: (defeatedUnits: Array<SiegeUnitLabelData>) => void;
	// maxOfflineBoostersLimit: ComputedRef<number>;
	// maxOfflineBoostersLimitUseLimit: ComputedRef<number>;
	// offlineBoosters: ComputedRef<number>;
	// offlineBoostersAvailableForUse: ComputedRef<number>;
	// increaseOfflineBoosters: (value: number) => void;
	// decreaseOfflineBoosters: (value: number) => void;
};

const DEFAULT_STORE_STATE = {
	tutorialSeen: false,
	stats: {
		startedAt: null,
		endedAt: null,
		damageDealt: 0,
		damageTaken: 0,
		enemiesDefeated: 0,
		enemiesTotal: 0,
	},
	result: null,
	siegeTargetTileIndexes: new Array<TileIndex>(),
	mainEnemyTileIndex: null,
	canEnemyAttackGuard: false,
	units: {},
	rewards: null,
	attackClicks: 0,
	movingUnitsIndex: null,
	defeatedUnits: [],
	currentBattlefield: {},
	// offlineBoosters: 10, // TODO: change to 0
};

const STORE_NAME = "siege-system";

export default defineStore(STORE_NAME, (): SiegeSystemStore => {
	const state = createImmutableState<SiegeSystemStoreState>({
		...DEFAULT_STORE_STATE,
	});

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): SiegeSystemStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<keyof SiegeSystemStoreState>;
		return keys.reduce(
			(
				acc: SiegeSystemStoreState,
				key: keyof SiegeSystemStoreState,
			): SiegeSystemStoreState => ({
				...acc,
				[key]: data[key],
			}),
			{} as SiegeSystemStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: SiegeSystemStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): SiegeSystemStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<SiegeSystemStoreState>): void => {
		setImmutableState(state, value);
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const reset = (): void => {
		setImmutableState(state, {
			...DEFAULT_STORE_STATE,
			tutorialSeen: state.value.tutorialSeen,
		});
		// uz nepotrebujeme ulozit rozohrate data, lebo fight skoncil
		$resetPayload();
	};

	const tutorialSeen = getImmutableStateProperty(
		state,
		"tutorialSeen",
		false,
	);

	const setTutorialSeen = (value: boolean): void => {
		setImmutableStateProperty(state, "tutorialSeen", value);
	};

	const stats = getImmutableStateProperty(state, "stats", {
		startedAt: null,
		endedAt: null,
		damageDealt: 0,
		damageTaken: 0,
		enemiesDefeated: 0,
		enemiesTotal: 0,
	});

	const setStartedAt = (value: number | null): void => {
		setImmutableStateProperty(state, "stats", {
			...state.value.stats,
			startedAt: value,
		});
	};

	const setEndedAt = (value: number | null): void => {
		setImmutableStateProperty(state, "stats", {
			...state.value.stats,
			endedAt: value,
		});
	};

	const increaseDamageDealt = (value: number): void => {
		//console.log("increaseDamageDealt", { value });
		if (isNaN(value) || value < 0)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				`Invalid value for resource damageDealt: ${value}`,
			);
		if (value === 0) return;

		setImmutableStateProperty(state, "stats", {
			...state.value.stats,
			damageDealt: (state.value.stats.damageDealt ?? 0) + value,
		});
	};

	const increaseDamageTaken = (value: number): void => {
		//console.log("increaseDamageTaken", { value });
		if (isNaN(value) || value < 0)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				`Invalid value for resource damageTaken: ${value}`,
			);

		setImmutableStateProperty(state, "stats", {
			...state.value.stats,
			damageTaken: (state.value.stats.damageTaken ?? 0) + value,
		});
	};

	const increaseEnemiesDefeated = (value = 1): void => {
		if (isNaN(value) || value < 0)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				`Invalid value for resource enemiesDefeated: ${value}`,
			);

		setImmutableStateProperty(state, "stats", {
			...state.value.stats,
			enemiesDefeated: (state.value.stats.enemiesDefeated ?? 0) + value,
		});
	};

	const setEnemiesTotal = (value: number): void => {
		setImmutableStateProperty(state, "stats", {
			...state.value.stats,
			enemiesTotal: value,
		});
	};

	const result = getImmutableStateProperty(state, "result", null);

	const setResult = (result: SiegeSystemResult | null): void => {
		setImmutableStateProperty(state, "result", result);
	};

	const siegeTargetTileIndexes = getImmutableStateProperty(
		state,
		"siegeTargetTileIndexes",
		[],
	);

	const setSiegeTargetTileIndexes = (tileIndexes: Array<TileIndex>): void => {
		if (!Array.isArray(tileIndexes)) return;
		setImmutableStateProperty(state, "siegeTargetTileIndexes", tileIndexes);
	};

	const addSiegeTargetTileIndex = (tileIndex: TileIndex): void => {
		if (state.value.siegeTargetTileIndexes.includes(tileIndex)) return;
		setImmutableStateProperty(state, "siegeTargetTileIndexes", [
			...state.value.siegeTargetTileIndexes,
			tileIndex,
		]);
	};

	const removeSiegeTargetTileIndex = (tileIndex: TileIndex): void => {
		if (!state.value.siegeTargetTileIndexes.includes(tileIndex)) return;
		setImmutableStateProperty(
			state,
			"siegeTargetTileIndexes",
			state.value.siegeTargetTileIndexes.filter(
				(index: TileIndex): boolean => index !== tileIndex,
			),
		);
	};

	const mainEnemyTileIndex = getImmutableStateProperty(
		state,
		"mainEnemyTileIndex",
		null,
	);

	const setMainEnemyTileIndex = (tileIndex: TileIndex | null): void =>
		setImmutableStateProperty(state, "mainEnemyTileIndex", tileIndex);

	const currentBattlefield = getImmutableStateProperty(
		state,
		"currentBattlefield",
		{},
	);

	const setCurrentBattlefield = (value: {
		tileIndex?: TileIndex;
		range?: number;
	}): void => setImmutableStateProperty(state, "currentBattlefield", value);

	const canEnemyAttackGuard = getImmutableStateProperty(
		state,
		"canEnemyAttackGuard",
		false,
	);

	const setCanEnemyAttackGuard = (value: boolean): void => {
		setImmutableStateProperty(state, "canEnemyAttackGuard", value);
	};

	const units = getImmutableStateProperty(state, "units", {});

	const setUnits = (units: SiegeSystemStoreState["units"] | null): void => {
		setImmutableStateProperty(state, "units", units ?? {});
	};

	const setUnit = (tileIndex: TileIndex, { hp }: { hp: number }): void => {
		const unitData = state.value.units?.[`${tileIndex}`];

		if (hp <= 0) {
			const units = { ...state.value.units };
			delete units[`${tileIndex}`];
			setImmutableStateProperty(state, "units", {
				...units,
			});
			return;
		}

		setImmutableStateProperty(state, "units", {
			...state.value.units,
			[`${tileIndex}`]: { ...unitData, hp },
		});
	};

	const removeUnit = (tileIndex: TileIndex): void => {
		const units = { ...state.value.units };
		delete units[`${tileIndex}`];
		setImmutableStateProperty(state, "units", {
			...units,
		});
	};

	const isUnitInStore = (tileIndex: TileIndex): boolean => {
		return `${tileIndex}` in state.value.units;
	};

	const rewards = computed<Reward | null>((): Reward | null => {
		if (
			!state.value.stats.endedAt ||
			state.value.result !== SiegeSystemResult.VICTORY
		)
			return null;
		return state.value.rewards;
	});

	const setRewards = (rewards: Reward | null): void =>
		setImmutableStateProperty(state, "rewards", rewards);

	const attackClicks = getImmutableStateProperty(state, "attackClicks", 0);

	const setAttackClicks = (attackClicks: number): void => {
		setImmutableStateProperty(state, "attackClicks", attackClicks);
	};

	const movingUnitsIndex = getImmutableStateProperty(
		state,
		"movingUnitsIndex",
		null,
	);

	const setMovingUnitsIndex = (movingUnitsIndex: TileIndex | null): void => {
		setImmutableStateProperty(state, "movingUnitsIndex", movingUnitsIndex);
	};

	const defeatedUnits = getImmutableStateProperty(state, "defeatedUnits", []);

	const setDefeatedUnits = (
		defeatedUnits: Array<SiegeUnitLabelData>,
	): void => {
		setImmutableStateProperty(state, "defeatedUnits", defeatedUnits);
	};

	// const maxOfflineBoostersLimit = computed<number>((): number => {
	// 	// TODO: apply upgrades
	// 	return 15;
	// });

	// const maxOfflineBoostersLimitUseLimit = computed<number>((): number => {
	// 	// TODO: apply upgrades
	// 	return 1;
	// });

	// const offlineBoosters = getImmutableStateProperty(
	// 	state,
	// 	"offlineBoosters",
	// 	0,
	// );

	// const offlineBoostersAvailableForUse = computed<number>((): number => {
	// 	return Math.min(
	// 		maxOfflineBoostersLimitUseLimit.value,
	// 		offlineBoosters.value,
	// 	);
	// });

	// const increaseOfflineBoosters = (value: number = 1): void =>
	// 	setImmutableStateProperty(
	// 		state,
	// 		"offlineBoosters",
	// 		Math.max(
	// 			0,
	// 			Math.min(
	// 				state.value.offlineBoosters + Math.abs(value),
	// 				maxOfflineBoostersLimit.value,
	// 			),
	// 		),
	// 	);

	// const decreaseOfflineBoosters = (value: number = 1): void =>
	// 	setImmutableStateProperty(
	// 		state,
	// 		"offlineBoosters",
	// 		Math.max(
	// 			0,
	// 			Math.min(
	// 				state.value.offlineBoosters - Math.abs(value),
	// 				maxOfflineBoostersLimit.value,
	// 			),
	// 		),
	// 	);

	return {
		$getPayload,
		$resetPayload,
		$setState,
		reset,
		tutorialSeen,
		setTutorialSeen,
		stats,
		setStartedAt,
		setEndedAt,
		increaseDamageDealt,
		increaseDamageTaken,
		increaseEnemiesDefeated,
		setEnemiesTotal,
		result,
		setResult,
		siegeTargetTileIndexes,
		setSiegeTargetTileIndexes,
		addSiegeTargetTileIndex,
		removeSiegeTargetTileIndex,
		mainEnemyTileIndex,
		setMainEnemyTileIndex,
		currentBattlefield,
		setCurrentBattlefield,
		canEnemyAttackGuard,
		setCanEnemyAttackGuard,
		units,
		setUnits,
		setUnit,
		removeUnit,
		isUnitInStore,
		rewards,
		setRewards,
		attackClicks,
		setAttackClicks,
		movingUnitsIndex,
		setMovingUnitsIndex,
		defeatedUnits,
		setDefeatedUnits,
		// maxOfflineBoostersLimit,
		// maxOfflineBoostersLimitUseLimit,
		// offlineBoosters,
		// offlineBoostersAvailableForUse,
		// increaseOfflineBoosters,
		// decreaseOfflineBoosters,
	};
});
